import { useContext, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { createScannedDocument } from '../../../../../../services/api/scannedDocuments';
import { useIntl } from 'react-intl';
import ScannedDocumentsLayout from '../../Commons/ScannedDocumentsLayout';
import { MainContext } from '../../../../../../context/MainContext';
import LayoutHeader from '../Commons/LayoutHeader';
import SuccessPage from '../../Commons/ScannedDocumentsLayout/components/SuccessPage';
import {
  getProjectTemplateById,
  updateProjectTemplate,
} from '../../../../../../services/api/projectTemplates';
import { useDispatch } from 'react-redux';
import { deletePreProcessedDocument } from '../../../../../../services/redux/preProcessedDocuments/actions';
import { getLaboratory } from '../../../../../../services/api/laboratory';
import laboratoryFieldGroups from '../Commons/laboratoryFieldGroups';
import { checkDiffTemplate } from '../../Commons/ScannedDocumentsLayout/utils.index';

const UpdateDeliveryScannedDoc = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { project } = useOutletContext();
  const { setLoading, setError } = useContext(MainContext);
  const { sdId, model, templateId, preprocessId } = useParams();
  const [initialData, setInitialData] = useState(null);
  const [success, setSuccess] = useState(false);
  const [diffTemplate, setDiffTemplate] = useState(false);
  const [template, setLoadedTemplate] = useState(null);
  const noTemplateMsg = intl.formatMessage({ id: 'noTemplate' });
  const noSupplierMsg = intl.formatMessage({ id: 'noSuppliers' });
  const navigate = useNavigate();

  const createDeliveryScannedDocument = async (
    regions,
    formState,
    fileName,
  ) => {
    try {
      const compressiveStrength = formState.compressive_strength;
      const defaultCompressiveStrength = {};
      const extraCompressiveStrength = {};

      compressiveStrength.forEach((item) => {
        const day = Number(item.day);
        if (day === 3 || day === 7 || day === 28 || day === 60) {
          Object.entries(item).forEach(([key, value]) => {
            if (key !== 'day') {
              defaultCompressiveStrength[
                `compressive_strength_${day}_${key.split('value')[1]}`
              ] = parseFloat(value);
            }
          });
        } else {
          Object.entries(item).forEach(([key, value]) => {
            if (key !== 'day') {
              extraCompressiveStrength[
                `compressive_strength_${day}_${key.split('value')[1]}`
              ] = parseFloat(value);
            }
          });
        }
      });

      const scannedDocument = await createScannedDocument(
        {
          fileName,
          template: templateId,
          pid: project.id_project,
          docket_number: formState.docket_number,
          regions,
          laboratory_note_data: {
            ...formState,
            ...defaultCompressiveStrength,
            strength_extra: extraCompressiveStrength,
          },
        },
        'laboratory',
      );

      dispatch(deletePreProcessedDocument(preprocessId));

      if (scannedDocument.data) {
        setSuccess(true);
        if (checkDiffTemplate(regions, template.regions)) {
          setDiffTemplate(regions);
        }
      }
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        setLoading(true);
        const loadedTemplate = (
          await getProjectTemplateById({ id: templateId })
        ).data;
        const loadedLab = (await getLaboratory(loadedTemplate.labId)).data;
        setLoadedTemplate(loadedTemplate);
        setInitialData({
          form: {
            templateName: loadedTemplate.name,
            supplier_id: loadedTemplate.supplier,
            phone_number: loadedLab.phone_number,
            email: loadedLab.email,
          },
          regions: loadedTemplate.regions,
        });
      } catch (error) {
        console.error('Error loading initial data:', error);
        setError('Error loading initial data');
      } finally {
        setLoading(false);
      }
    };
    loadTemplate();
  }, [
    setLoading,
    setInitialData,
    sdId,
    noTemplateMsg,
    noSupplierMsg,
    model,
    setError,
    templateId,
  ]);

  if (success) {
    return (
      <SuccessPage
        title={intl.formatMessage({
          id: 'pages.ocr.scannedDocuments.laboratory.create.success.title',
        })}
        subtitle={intl.formatMessage({
          id: 'pages.ocr.scannedDocuments.laboratory.create.success.subtitle',
        })}
        checkDiffTemplate={Boolean(diffTemplate)}
        onBackToOcr={() =>
          navigate(`/app/project/${project.id_project}/quality/ocr`)
        }
        onSaveTemplate={async () => {
          const newTemplate = { ...template, regions: diffTemplate };
          setLoading(true);
          await updateProjectTemplate(newTemplate);
          setLoading(false);
          navigate(`/../../ocr`, {
            replace: true,
          });
        }}
      />
    );
  }
  return (
    <ScannedDocumentsLayout
      model="laboratory"
      initialData={initialData}
      fieldGroups={laboratoryFieldGroups}
      validableFields={laboratoryFieldGroups.mandatory}
      onFinish={createDeliveryScannedDocument}
      onBack={() =>
        navigate(`/app/project/${project.id_project}/quality/ocr/preprocess`)
      }
      mode="edit"
      header={
        <LayoutHeader
          template={initialData?.form?.templateName}
          supplier={initialData?.form?.supplier_id}
          phone={initialData?.form?.phone_number}
          email={initialData?.form?.email}
        />
      }
    />
  );
};

export default UpdateDeliveryScannedDoc;
